import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Bike, Pencil, Plus, Search as SearchIcon, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { adminRiderApi, type Rider } from "@/features/delivery/api";
import { formatApiError } from "@/features/auth/formatError";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SubmitButton } from "@/components/form/SubmitButton";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";

const VEHICLES = ["bike", "scooter", "bicycle", "car"];

type FormState = {
    id?: string;
    full_name: string;
    email: string;
    phone: string;
    password: string;
    vehicle_type: string;
    vehicle_number: string;
    photo_url: string;
    zone: string;
};

function emptyForm(): FormState {
    return {
        full_name: "",
        email: "",
        phone: "",
        password: "",
        vehicle_type: "bike",
        vehicle_number: "",
        photo_url: "",
        zone: "",
    };
}

export default function AdminRiders() {
    const qc = useQueryClient();
    const [search, setSearch] = useState("");
    const [online, setOnline] = useState<string>("");
    const [dialog, setDialog] = useState(false);
    const [form, setForm] = useState<FormState>(emptyForm);
    const [error, setError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);

    const { data, isLoading } = useQuery({
        queryKey: ["admin", "riders", search, online],
        queryFn: () =>
            adminRiderApi.list({
                search: search || undefined,
                online: online === "" ? undefined : online === "yes",
                page_size: 50,
            }),
    });

    const { data: analytics } = useQuery({
        queryKey: ["admin", "riders", "analytics"],
        queryFn: adminRiderApi.analytics,
        refetchInterval: 20000,
    });

    const delMut = useMutation({
        mutationFn: (id: string) => adminRiderApi.remove(id),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ["admin", "riders"] });
            toast.success("Rider deleted");
        },
        onError: (err) => toast.error(formatApiError(err, "Couldn't delete rider")),
    });

    const openNew = () => {
        setForm(emptyForm());
        setError(null);
        setDialog(true);
    };
    const openEdit = (r: Rider) => {
        setForm({
            id: r.id,
            full_name: r.full_name,
            email: r.email,
            phone: r.phone,
            password: "",
            vehicle_type: r.vehicle_type,
            vehicle_number: r.vehicle_number,
            photo_url: r.photo_url,
            zone: r.zone,
        });
        setError(null);
        setDialog(true);
    };

    const onSave = async (e: React.FormEvent) => {
        e.preventDefault();
        setSaving(true);
        setError(null);
        try {
            if (form.id) {
                await adminRiderApi.update(form.id, {
                    full_name: form.full_name,
                    phone: form.phone,
                    vehicle_type: form.vehicle_type,
                    vehicle_number: form.vehicle_number,
                    photo_url: form.photo_url,
                    zone: form.zone,
                });
                toast.success("Rider updated");
            } else {
                await adminRiderApi.create({
                    full_name: form.full_name,
                    email: form.email,
                    phone: form.phone,
                    password: form.password,
                    vehicle_type: form.vehicle_type,
                    vehicle_number: form.vehicle_number,
                    photo_url: form.photo_url,
                    zone: form.zone,
                });
                toast.success("Rider added");
            }
            qc.invalidateQueries({ queryKey: ["admin", "riders"] });
            setDialog(false);
        } catch (err) {
            setError(formatApiError(err, "Save failed"));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="space-y-5" data-testid="admin-riders-page">
            <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                    <h1 className="text-2xl font-extrabold sm:text-3xl">Riders</h1>
                    <p className="text-sm text-muted-foreground">
                        {analytics
                            ? `${analytics.total_riders} riders · ${analytics.online_riders} online · ${analytics.active_deliveries} active deliveries`
                            : "Loading…"}
                    </p>
                </div>
                <button
                    onClick={openNew}
                    data-testid="admin-rider-new"
                    className="inline-flex items-center gap-2 rounded-full bg-brand px-4 py-2 text-sm font-bold text-white shadow-brand hover:bg-brand-600"
                >
                    <Plus className="h-4 w-4" /> Add rider
                </button>
            </div>

            <div className="flex flex-wrap gap-3">
                <div className="relative flex-1 min-w-[240px]">
                    <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                        className="pl-9"
                        placeholder="Search by name, email, or phone…"
                        data-testid="admin-riders-search"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>
                <select
                    value={online}
                    onChange={(e) => setOnline(e.target.value)}
                    data-testid="admin-riders-online-filter"
                    className="h-10 rounded-full border border-border bg-background px-3 text-sm"
                >
                    <option value="">All statuses</option>
                    <option value="yes">Online</option>
                    <option value="no">Offline</option>
                </select>
            </div>

            <div className="overflow-x-auto rounded-2xl border border-border/70 bg-card">
                <table className="min-w-full text-sm">
                    <thead className="bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
                        <tr>
                            <th className="px-4 py-3">Rider</th>
                            <th className="px-4 py-3">Vehicle</th>
                            <th className="px-4 py-3">Zone</th>
                            <th className="px-4 py-3">Deliveries</th>
                            <th className="px-4 py-3">Earnings</th>
                            <th className="px-4 py-3">Status</th>
                            <th className="px-4 py-3 text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-border/60">
                        {isLoading ? (
                            <tr><td colSpan={7} className="p-6 text-center text-sm text-muted-foreground">Loading…</td></tr>
                        ) : data && data.items.length ? (
                            data.items.map((r) => (
                                <tr key={r.id} data-testid={`admin-rider-row-${r.id}`}>
                                    <td className="px-4 py-3">
                                        <div className="flex items-center gap-3">
                                            {r.photo_url ? (
                                                <img src={r.photo_url} alt="" className="h-9 w-9 rounded-full object-cover" />
                                            ) : (
                                                <span className="grid h-9 w-9 place-items-center rounded-full bg-brand/10 text-brand">
                                                    <Bike className="h-4 w-4" />
                                                </span>
                                            )}
                                            <div>
                                                <p className="font-semibold">{r.full_name}</p>
                                                <p className="text-[11px] text-muted-foreground">{r.email}</p>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-4 py-3 text-xs uppercase">
                                        {r.vehicle_type}
                                        <p className="text-[10px] text-muted-foreground">{r.vehicle_number}</p>
                                    </td>
                                    <td className="px-4 py-3 text-xs">{r.zone || "—"}</td>
                                    <td className="px-4 py-3 font-semibold">{r.total_deliveries}</td>
                                    <td className="px-4 py-3 font-semibold">₹{(r.total_earnings || 0).toLocaleString("en-IN")}</td>
                                    <td className="px-4 py-3">
                                        <span
                                            className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-[11px] font-semibold ${
                                                r.is_online ? "bg-emerald-500/10 text-emerald-700" : "bg-slate-500/10 text-slate-600"
                                            }`}
                                        >
                                            <span className={`h-1.5 w-1.5 rounded-full ${r.is_online ? "bg-emerald-500" : "bg-slate-400"}`} />
                                            {r.is_online ? "Online" : "Offline"}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3">
                                        <div className="flex justify-end gap-1">
                                            <button
                                                onClick={() => openEdit(r)}
                                                data-testid={`admin-rider-edit-${r.id}`}
                                                className="grid h-8 w-8 place-items-center rounded-lg hover:bg-muted"
                                            >
                                                <Pencil className="h-4 w-4" />
                                            </button>
                                            <button
                                                onClick={() => {
                                                    if (window.confirm(`Delete ${r.full_name}?`)) delMut.mutate(r.id);
                                                }}
                                                data-testid={`admin-rider-delete-${r.id}`}
                                                className="grid h-8 w-8 place-items-center rounded-lg text-error hover:bg-error/10"
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr><td colSpan={7} className="p-6 text-center text-sm text-muted-foreground">No riders yet.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            <Dialog open={dialog} onOpenChange={setDialog}>
                <DialogContent data-testid="admin-rider-dialog" className="sm:max-w-lg">
                    <form onSubmit={onSave}>
                        <DialogHeader>
                            <DialogTitle>{form.id ? "Edit rider" : "Add rider"}</DialogTitle>
                        </DialogHeader>
                        <div className="mt-4 space-y-3">
                            <div className="grid gap-3 sm:grid-cols-2">
                                <div>
                                    <Label>Full name *</Label>
                                    <Input data-testid="admin-rider-name" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} required minLength={2} />
                                </div>
                                <div>
                                    <Label>Phone *</Label>
                                    <Input data-testid="admin-rider-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} required />
                                </div>
                            </div>
                            {form.id ? null : (
                                <>
                                    <div>
                                        <Label>Email * (used for login)</Label>
                                        <Input type="email" data-testid="admin-rider-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
                                    </div>
                                    <div>
                                        <Label>Temporary password *</Label>
                                        <Input type="password" data-testid="admin-rider-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required minLength={6} />
                                    </div>
                                </>
                            )}
                            <div className="grid gap-3 sm:grid-cols-3">
                                <div>
                                    <Label>Vehicle</Label>
                                    <select
                                        className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                                        value={form.vehicle_type}
                                        onChange={(e) => setForm({ ...form, vehicle_type: e.target.value })}
                                    >
                                        {VEHICLES.map((v) => <option key={v}>{v}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <Label>Vehicle number</Label>
                                    <Input value={form.vehicle_number} onChange={(e) => setForm({ ...form, vehicle_number: e.target.value })} />
                                </div>
                                <div>
                                    <Label>Zone</Label>
                                    <Input value={form.zone} onChange={(e) => setForm({ ...form, zone: e.target.value })} placeholder="Koramangala" />
                                </div>
                            </div>
                            <div>
                                <Label>Photo URL (optional)</Label>
                                <Input value={form.photo_url} onChange={(e) => setForm({ ...form, photo_url: e.target.value })} placeholder="https://…" />
                            </div>
                            {error ? <p data-testid="admin-rider-error" className="text-xs font-medium text-error">{error}</p> : null}
                        </div>
                        <DialogFooter className="mt-4 gap-2">
                            <button type="button" onClick={() => setDialog(false)} className="rounded-full border border-border px-4 py-2 text-sm font-semibold hover:bg-muted">Cancel</button>
                            <SubmitButton type="submit" fullWidth={false} loading={saving} data-testid="admin-rider-save">Save</SubmitButton>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
}
